/**
 * Idle Merge rules (v1.43.0, Merge Board 2.0 — v1.45.0) — client preview only, the server decides.
 * A board piece is either a hero part (UserModuleDTO, has `module`) or a fragment (FragmentDTO, has `configId`).
 * Parts: same body type AND same level L merge into L + 1; stats are summed.
 * Fragments: same config AND same level AND not whole → level + 1 (1/4 + 1/4 → 2/4), free.
 * Parts and fragments never merge with each other (on the board they swap places).
 * Keep in sync with api-service IdleMergeMath.fuse / cost.
 */
import i18n from '../../../i18n';

const t = (...args) => i18n.t(...args);

export const BOARD_SIZE = 7;

const typeOf = (item) => item?.module?.type || null;

/** FragmentDTO (chest / key / ticket piece) rather than a hero part */
export const isFragment = (piece) => !!piece && !piece.module && piece.configId != null;

export const sameHero = (a, b) => !!a?.character && a.character === b?.character;

/** Part: fuse level (1 = catalog part). Fragment: its level (1 = 1/denominator). */
export const levelOf = (item) => (isFragment(item) ? item.level || 1 : item?.fuseLevel || 1);

/** "1/4", "2/4" … of a fragment */
export const fractionText = (f) => `${f?.numerator ?? 1}/${f?.denominator ?? 1}`;

/** Fragment of the same item, same size, both not whole — the pair that merges. */
function fragmentBlocker(source, target) {
  if (source.configId !== target.configId) return t('merge.block.other_item', 'Only pieces of the same item can be merged');
  if (source.whole || target.whole) return t('merge.block.whole', 'This item is whole — tap it to claim');
  if (levelOf(source) !== levelOf(target)) return t('merge.block.piece_size', 'Only pieces of the same size can be merged');
  return null;
}

/**
 * Why `source` can't be dropped onto `target` to merge (null = it can).
 * Parts: same body part AND same level, L + L → L + 1 (server IdleMergeMath.fuse).
 * Fragments: same config AND same level AND not whole.
 */
export function mergeBlocker(source, target, maxLevel = Infinity) {
  if (!source || !target || source === target) return 'self';
  const fs = isFragment(source);
  if (fs !== isFragment(target)) return t('merge.block.mixed', 'Parts and item pieces do not merge');
  if (fs) return fragmentBlocker(source, target);
  if (!typeOf(source) || typeOf(source) !== typeOf(target)) return t('merge.block.part_type', 'Only the same body part can be merged');
  if (levelOf(source) !== levelOf(target)) return t('merge.block.part_level', 'Only parts of the same level can be merged');
  if (levelOf(target) + 1 > maxLevel) return t('merge.block.max_level', 'Level {{n}} is the highest', { n: maxLevel });
  return null;
}

/** Can `source` be dropped onto `target` to merge? */
export function canMerge(source, target, maxLevel = Infinity) {
  return mergeBlocker(source, target, maxLevel) === null;
}

/** Same kind of thing (same body part / same item) — a drop that "looked like" a merge attempt. */
export function looksAlike(a, b) {
  if (!a || !b || isFragment(a) !== isFragment(b)) return false;
  return isFragment(a) ? a.configId === b.configId : typeOf(a) === typeOf(b);
}

/** coins = coinCost × (level − 1) × (1 − discount), rounded up (LairMath.mergeCost) */
export function mergeCost(rules, level) {
  const base = Math.max(0, Number(rules?.coinCost) || 0) * Math.max(0, level - 1);
  if (base <= 0) return 0;
  const d = Math.min(0.95, Math.max(0, Number(rules?.mergeDiscount) || 0));
  return Math.ceil(Math.round(base * (1 - d) * 1e6) / 1e6);
}

/** Fragment merge preview: {fragment: true, level, numerator, denominator, whole, name, cost: 0} */
export function previewFragmentMerge(source, target) {
  const level = levelOf(target) + 1;
  const numerator = (Number(target.numerator) || 1) * 2;
  const denominator = Number(target.denominator) || 1;
  const whole = target.wholeLevel ? level >= target.wholeLevel : numerator >= denominator;
  return { fragment: true, level, numerator, denominator, whole, name: target.name, cost: 0 };
}

/**
 * Preview of `source` merged into `target`.
 * Parts: {str, agi, int, power, level, sameHero, perkType, perkValue, cost}; fragments: see previewFragmentMerge.
 */
export function previewMerge(source, target, rules) {
  if (isFragment(target)) return previewFragmentMerge(source, target);
  const same = sameHero(source, target);
  const k = same ? 1 + Math.max(0, Number(rules?.sameHeroBonusPercent) || 0) / 100 : 1;
  const sum = (key) => Math.round(((Number(target[key]) || 0) + (Number(source[key]) || 0)) * k);
  let perkType = target.perkType || null;
  let perkValue = Number(target.perkValue) || 0;
  if (!perkType) {
    perkType = source.perkType || null;
    perkValue = Number(source.perkValue) || 0;
  } else if (perkType === source.perkType) {
    perkValue += Number(source.perkValue) || 0;
  }
  const level = levelOf(target) + 1;
  const str = sum('str');
  const agi = sum('agi');
  const intel = sum('int');
  return {
    str, agi, int: intel, power: str + agi + intel, level, sameHero: same,
    perkType, perkValue: Math.round(perkValue * 10000) / 10000, cost: mergeCost(rules, level),
  };
}

/** cells[] → {"x,y": piece} (piece = part item or fragment) */
export function cellMap(cells) {
  const m = {};
  for (const c of cells || []) {
    const piece = c.item || c.fragment;
    if (piece) m[`${c.x},${c.y}`] = piece;
  }
  return m;
}

/** Cell index in the server's unlocked[] / buyable[] arrays */
export const cellIndex = (x, y, width = BOARD_SIZE) => y * width + x;

/** Is the cell open? Boards without `unlocked` (pre-v1.45.0) are fully open. */
export function isUnlocked(board, x, y) {
  const arr = board?.unlocked;
  if (!Array.isArray(arr)) return true;
  return !!arr[cellIndex(x, y, board.width || BOARD_SIZE)];
}

export function isBuyable(board, x, y) {
  const arr = board?.buyable;
  return Array.isArray(arr) && !!arr[cellIndex(x, y, board.width || BOARD_SIZE)];
}

/**
 * First mergeable pair on the board (reading order) for the idle hint: ["x,y", "x,y"] or null.
 * Parts: same type + level within maxLevel; fragments: same config + level, not whole.
 */
export function findMergePair(cells, maxLevel = Infinity, width = BOARD_SIZE) {
  const keys = Object.keys(cells || {}).sort((a, b) => {
    const [ax, ay] = a.split(',').map(Number);
    const [bx, by] = b.split(',').map(Number);
    return cellIndex(ax, ay, width) - cellIndex(bx, by, width);
  });
  for (let i = 0; i < keys.length; i += 1) {
    for (let j = i + 1; j < keys.length; j += 1) {
      if (canMerge(cells[keys[i]], cells[keys[j]], maxLevel)) return [keys[i], keys[j]];
    }
  }
  return null;
}

/** Spawn countdown: ms until `nextAt` on the server clock (offset = server − client). */
export function msUntil(nextAtIso, offsetMs = 0, nowMs = Date.now()) {
  const t = Date.parse(nextAtIso);
  if (Number.isNaN(t)) return null;
  return t - (nowMs + offsetMs);
}

/** 754_000 → "12:34", 3_725_000 → "1:02:05" */
export function formatCountdown(ms) {
  const s = Math.max(0, Math.ceil((ms || 0) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

/** 1250 → "1.3k", 12000 → "12k" (fits a 44px cell) */
export function compactNumber(n) {
  const v = Number(n) || 0;
  if (v < 1000) return String(v);
  if (v < 10000) return `${(Math.round(v / 100) / 10).toString()}k`;
  if (v < 1e6) return `${Math.round(v / 1000)}k`;
  return `${Math.round(v / 1e5) / 10}M`;
}
